/**
 * Module 33 — render a background's params to CSS.
 *
 * Output composes UNDER the frozen tokens exactly as the current system does
 * (docs/Framework_Catalogs/Background_Rules.md). Layer order top→bottom:
 * `var(--bg-bloom)`, `var(--bg-halftone)`, optional soft glow, optional very-soft
 * vignette, the watermark **motif(s)**, then the theme's base linear-gradient last.
 *
 * The SAME function feeds the live preview and the Copy-CSS button, so what you
 * see is exactly what you paste into `global.css` / a `[data-bg]` variant.
 * `blur` remains metadata only in v1 (no raster).
 */
import { clamp01, hexToHsl, hslToHex, wrapHue } from './color';
import { renderMotifLayers } from './motifs';
function hexToRgb(hex) {
    const c = hex.replace('#', '');
    return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
}
function rgba(hex, alpha) {
    const [r, g, b] = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${Number(alpha.toFixed(3))})`;
}
const centered = (image) => ({
    image,
    size: 'auto',
    repeat: 'no-repeat',
    position: 'center',
});
/**
 * The base gradient layer(s) for the chosen style (Batch 5). Returned bottom-most
 * (pushed last). Aurora returns several soft colour blobs over a base wash.
 */
function baseLayers(bg) {
    // Solid mode (enableGradient === false): a flat fill of the Base Color only.
    // A `primary→primary` linear-gradient keeps the layer structure identical to
    // the gradient path so size/repeat/position and Copy-CSS all stay consistent.
    if (bg.enableGradient === false) {
        return [centered(`linear-gradient(${bg.primary}, ${bg.primary})`)];
    }
    const angle = Math.round(bg.gradientAngle);
    const { h, s } = hexToHsl(bg.primary);
    switch (bg.gradientStyle) {
        case 'radial':
            return [
                centered(`radial-gradient(ellipse 130% 110% at 50% 22%, ${bg.secondary}, ${bg.primary})`),
            ];
        case 'holographic': {
            // Iridescent pastel band cycling hues around the base hue.
            const light = 0.84;
            const sat = clamp01(Math.max(0.4, Math.min(0.62, s)));
            const deltas = [-70, -30, 10, 50, 95];
            const stops = deltas
                .map((d, i) => `${hslToHex({ h: wrapHue(h + d), s: sat, l: light })} ${Math.round((i / (deltas.length - 1)) * 100)}%`)
                .join(', ');
            return [centered(`linear-gradient(${angle}deg, ${stops})`)];
        }
        case 'aurora': {
            const sat = clamp01(Math.max(0.42, Math.min(0.6, s)));
            const blob = (dh, x, y, r) => centered(`radial-gradient(circle at ${x}% ${y}%, ${rgba(hslToHex({ h: wrapHue(h + dh), s: sat, l: 0.82 }), 0.75)}, transparent ${r}%)`);
            return [
                blob(60, 22, 28, 55),
                blob(-45, 82, 22, 55),
                blob(120, 60, 82, 58),
                centered(`linear-gradient(${angle}deg, ${bg.primary}, ${bg.secondary})`),
            ];
        }
        default:
            return [centered(`linear-gradient(${angle}deg, ${bg.primary}, ${bg.secondary})`)];
    }
}
// Phase offsets (px) that de-align mixed motifs so they interleave instead of
// stacking in the same tile cells. Prime-ish, non-repeating; index 0 = no shift.
const MIX_OFFSETS = ['0 0', '53px 37px', '29px 83px', '89px 17px', '13px 61px', '71px 101px'];
export function toCss(bg) {
    const layers = [
        { image: 'var(--bg-bloom)', size: 'auto', repeat: 'no-repeat', position: 'center' },
        {
            image: 'var(--bg-halftone)',
            size: 'var(--bg-halftone-size)',
            repeat: 'repeat',
            position: 'center',
        },
    ];
    if (bg.accent !== undefined && bg.glowStrength > 0.05) {
        layers.push({
            image: `radial-gradient(ellipse 60% 45% at 50% 38%, ${rgba(bg.accent, bg.glowStrength)}, transparent 70%)`,
            size: 'auto',
            repeat: 'no-repeat',
            position: 'center',
        });
    }
    if (bg.vignette > 0.03) {
        layers.push({
            image: `radial-gradient(ellipse 120% 120% at 50% 50%, transparent 55%, rgba(0, 0, 0, ${Number((bg.vignette * 0.22).toFixed(3))}))`,
            size: 'auto',
            repeat: 'no-repeat',
            position: 'center',
        });
    }
    // The watermark texture — the primary motif plus any mixed-in motifs. Each
    // motif's layers get a distinct phase offset so a mix (e.g. bubbles+droplets)
    // interleaves instead of overlapping in the same tile positions.
    if (bg.textureOpacity > 0.005) {
        const motifIds = [bg.textureType, ...(bg.textureMix ?? [])].filter((id) => id !== 'none');
        motifIds.forEach((id, i) => {
            const position = MIX_OFFSETS[i % MIX_OFFSETS.length];
            for (const layer of renderMotifLayers(id, bg.textureOpacity, bg.textureScale)) {
                layers.push({ ...layer, position });
            }
        });
    }
    for (const layer of baseLayers(bg))
        layers.push(layer);
    const backgroundImage = layers.map((l) => l.image).join(', ');
    const backgroundSize = layers.map((l) => l.size).join(', ');
    const backgroundRepeat = layers.map((l) => l.repeat).join(', ');
    const backgroundPosition = layers.map((l) => l.position).join(', ');
    const indented = layers.map((l) => `    ${l.image}`).join(',\n');
    const block = [
        `/* theme: ${bg.theme} · seed ${bg.seed} · v${bg.version} · texture: ${bg.textureType} */`,
        `background-image:`,
        `${indented};`,
        `background-size: ${backgroundSize};`,
        `background-repeat: ${backgroundRepeat};`,
        `background-position: ${backgroundPosition};`,
    ].join('\n');
    return { backgroundImage, backgroundSize, backgroundRepeat, backgroundPosition, block };
}
